import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

interface DebugLog {
  time: string;
  message: string;
  type: 'info' | 'success' | 'error';
}

interface DebugPanelProps {
  nextPrayerTime?: string;
  nextPrayerName?: string;
  currentTime?: string;
}

export function DebugPanel({ nextPrayerTime, nextPrayerName, currentTime }: DebugPanelProps) {
  const [logs, setLogs] = useState<DebugLog[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('unknown');
  const [serviceWorkerStatus, setServiceWorkerStatus] = useState('unknown');
  const [audioUnlocked, setAudioUnlocked] = useState('unknown');
  const [browserInfo, setBrowserInfo] = useState('');

  useEffect(() => {
    // Detect browser
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Edg')) browser = 'Edge';
    setBrowserInfo(`${browser} | ${ua.includes('Mobile') ? 'Mobile' : 'Desktop'}`);

    // Check notification permission
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    } else {
      setNotificationPermission('not supported');
    }

    // Check service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(() => {
        setServiceWorkerStatus('ready');
      }).catch(() => {
        setServiceWorkerStatus('error');
      });
    } else {
      setServiceWorkerStatus('not supported');
    }

    // Intercept console.log
    const originalLog = console.log;
    const originalError = console.error;

    console.log = (...args) => {
      originalLog(...args);
      const message = args.join(' ');

      // Update audio unlock status
      if (message.includes('Audio unlocked successfully')) {
        setAudioUnlocked('unlocked');
      } else if (message.includes('Audio unlock pending')) {
        setAudioUnlocked('pending');
      }

      if (message.includes('🔔') || message.includes('🎵') || message.includes('📢') ||
          message.includes('✅') || message.includes('❌') || message.includes('▶️') ||
          message.includes('🔓') || message.includes('📦') || message.includes('⚠️')) {
        const now = new Date();
        const timeStr = `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;

        let type: 'info' | 'success' | 'error' = 'info';
        if (message.includes('✅')) type = 'success';
        if (message.includes('❌') || message.includes('⚠️')) type = 'error';

        setLogs(prev => [...prev.slice(-29), { time: timeStr, message, type }]);
      }
    };

    console.error = (...args) => {
      originalError(...args);
      const message = args.join(' ');
      if (message.includes('🔔') || message.includes('🎵') || message.includes('📢') ||
          message.includes('✅') || message.includes('❌')) {
        const now = new Date();
        const timeStr = `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
        setLogs(prev => [...prev.slice(-29), { time: timeStr, message, type: 'error' }]);
      }
    };

    return () => {
      console.log = originalLog;
      console.error = originalError;
    };
  }, []);

  return (
    <>
      {/* Toggle Button - Only show when panel is hidden */}
      {!isVisible && (
        <button
          onClick={() => setIsVisible(true)}
          className="fixed bottom-4 left-4 z-50 bg-blue-500 text-white px-4 py-2 rounded-full shadow-lg font-bold text-sm"
        >
          Debug
        </button>
      )}

      {/* Debug Panel */}
      {isVisible && (
        <div className="fixed top-0 left-0 z-50 bg-white rounded-lg shadow-2xl w-[90vw] max-w-sm max-h-[50vh] m-4 overflow-hidden flex flex-col">
          <div className="bg-blue-500 text-white px-4 py-2 flex items-center justify-between">
            <span className="font-bold">Debug Panel</span>
            <button onClick={() => setIsVisible(false)} className="p-1">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-3 bg-gray-50 border-b border-gray-200 text-xs space-y-1">
            <div className="flex justify-between">
              <span className="font-semibold">Browser:</span>
              <span className="font-bold text-blue-600">{browserInfo}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-semibold">Notifications:</span>
              <span className={`font-bold ${
                notificationPermission === 'granted' ? 'text-green-600' :
                notificationPermission === 'denied' ? 'text-red-600' : 'text-orange-600'
              }`}>
                {notificationPermission}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="font-semibold">Service Worker:</span>
              <span className={`font-bold ${
                serviceWorkerStatus === 'ready' ? 'text-green-600' : 'text-red-600'
              }`}>
                {serviceWorkerStatus}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="font-semibold">Audio Status:</span>
              <span className={`font-bold ${
                audioUnlocked === 'unlocked' ? 'text-green-600' :
                audioUnlocked === 'pending' ? 'text-orange-600' : 'text-gray-600'
              }`}>
                {audioUnlocked}
              </span>
            </div>
            {currentTime && (
              <div className="flex justify-between bg-blue-100 p-2 rounded mt-2">
                <span className="font-semibold">Current Time:</span>
                <span className="font-bold text-blue-800">{currentTime}</span>
              </div>
            )}
            {nextPrayerName && nextPrayerTime && (
              <div className="flex justify-between bg-green-100 p-2 rounded">
                <span className="font-semibold">Next Prayer:</span>
                <span className="font-bold text-green-800">{nextPrayerName} @ {nextPrayerTime}</span>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {logs.length === 0 ? (
              <div className="text-gray-400 text-center py-8 text-sm">
                Waiting for events...
              </div>
            ) : (
              logs.map((log, i) => (
                <div
                  key={i}
                  className={`text-xs p-2 rounded ${
                    log.type === 'success' ? 'bg-green-50 text-green-800' :
                    log.type === 'error' ? 'bg-red-50 text-red-800' :
                    'bg-blue-50 text-blue-800'
                  }`}
                >
                  <span className="font-mono font-bold">{log.time}</span>
                  <div className="mt-1">{log.message}</div>
                </div>
              ))
            )}
          </div>

          <div className="p-2 bg-gray-50 border-t border-gray-200 space-y-2">
            <div className="bg-yellow-50 border border-yellow-200 p-2 rounded text-xs">
              <p className="font-semibold text-yellow-800 mb-1">Audio Context Info:</p>
              <p className="text-yellow-700 mb-1">If audio stops working after days of running:</p>
              <ol className="text-yellow-700 list-decimal list-inside space-y-1">
                <li>Tap/click the app to wake up the audio context</li>
                <li>Click a "Test Audio" button below to unlock</li>
                <li>Use "Force Clear Notified Prayers" if it keeps missing sounds</li>
              </ol>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  const testAudio = new Audio('/audio/ding.mp3');
                  testAudio.volume = 0.5;
                  testAudio.play().then(() => {
                    console.log('✅ Test ding played successfully');
                    setAudioUnlocked('unlocked');
                  }).catch((err) => {
                    console.log('❌ Test ding failed:', err.message);
                    setAudioUnlocked('blocked');
                  });
                }}
                className="bg-blue-500 text-white px-3 py-2 rounded text-xs font-semibold hover:bg-blue-600"
              >
                Test Ding
              </button>
              <button
                onClick={() => {
                  const testAudio = new Audio('/audio/azan.mp3');
                  testAudio.volume = 0.5;
                  testAudio.play().then(() => {
                    console.log('✅ Test azan played successfully');
                  }).catch((err) => {
                    console.log('❌ Test azan failed:', err.message);
                  });
                }}
                className="bg-green-500 text-white px-3 py-2 rounded text-xs font-semibold hover:bg-green-600"
              >
                Test Azan
              </button>
              <button
                onClick={() => {
                  const testAudio = new Audio('/audio/boom.wav');
                  testAudio.volume = 0.5;
                  testAudio.play().then(() => {
                    console.log('✅ Test boom played successfully');
                  }).catch((err) => {
                    console.log('❌ Test boom failed:', err.message);
                  });
                }}
                className="bg-purple-500 text-white px-3 py-2 rounded text-xs font-semibold hover:bg-purple-600"
              >
                Test Boom
              </button>
              <button
                onClick={() => {
                  const testAudio = new Audio('/audio/doaa.mp3');
                  testAudio.volume = 0.5;
                  testAudio.play().then(() => {
                    console.log('✅ Test doaa played successfully');
                  }).catch((err) => {
                    console.log('❌ Test doaa failed:', err.message);
                  });
                }}
                className="bg-orange-500 text-white px-3 py-2 rounded text-xs font-semibold hover:bg-orange-600"
              >
                Test Doaa
              </button>
            </div>
            <button
              onClick={() => {
                console.log('🧪 SIMULATING FULL AZAN SEQUENCE');
                const playSequence = async () => {
                  console.log('▶️ Step 1: Playing azan.mp3');
                  const azan = new Audio('/audio/azan.mp3');
                  azan.volume = 0.5;
                  await azan.play();
                  await new Promise(resolve => {
                    azan.onended = resolve;
                  });

                  console.log('▶️ Step 2: Playing doaa.mp3');
                  const doaa = new Audio('/audio/doaa.mp3');
                  doaa.volume = 0.5;
                  await doaa.play();
                  await new Promise(resolve => {
                    doaa.onended = resolve;
                  });

                  console.log('✅ Full sequence completed');
                };
                playSequence().catch(err => console.error('❌ Sequence failed:', err));
              }}
              className="w-full bg-red-500 text-white px-3 py-2 rounded text-xs font-semibold hover:bg-red-600"
            >
              Test Full Azan Sequence
            </button>
            <button
              onClick={() => {
                console.log('💥 SIMULATING MAGHRIB BOOM SEQUENCE');
                const playSequence = async () => {
                  console.log('▶️ Step 1: Playing boom.wav');
                  const boom = new Audio('/audio/boom.wav');
                  boom.volume = 0.5;
                  await boom.play();
                  await new Promise(resolve => {
                    boom.onended = resolve;
                  });

                  console.log('▶️ Step 2: Playing azan.mp3');
                  const azan = new Audio('/audio/azan.mp3');
                  azan.volume = 0.5;
                  await azan.play();
                  await new Promise(resolve => {
                    azan.onended = resolve;
                  });

                  console.log('▶️ Step 3: Playing doaa.mp3');
                  const doaa = new Audio('/audio/doaa.mp3');
                  doaa.volume = 0.5;
                  await doaa.play();
                  await new Promise(resolve => {
                    doaa.onended = resolve;
                  });

                  console.log('✅ Maghrib boom sequence completed');
                };
                playSequence().catch(err => console.error('❌ Boom sequence failed:', err));
              }}
              className="w-full bg-orange-500 text-white px-3 py-2 rounded text-xs font-semibold hover:bg-orange-600"
            >
              💥 Test Maghrib Boom
            </button>
            <button
              onClick={async () => {
                console.log('🧹 Clearing notification state...');
                // Clear service worker notifications
                if ('serviceWorker' in navigator) {
                  const registration = await navigator.serviceWorker.ready;
                  const notifications = await registration.getNotifications();
                  console.log(`📱 Found ${notifications.length} pending notifications`);
                  notifications.forEach(notification => {
                    console.log(`🗑️ Closing notification: ${notification.tag}`);
                    notification.close();
                  });
                  console.log('✅ All notifications cleared');
                }
                // Clear localStorage notification state
                const keys = Object.keys(localStorage);
                const notifKeys = keys.filter(k => k.includes('notif') || k.includes('prayer'));
                console.log(`🗑️ Clearing ${notifKeys.length} localStorage keys`);
                notifKeys.forEach(key => localStorage.removeItem(key));

                alert('Notification state cleared! Please reload the app.');
              }}
              className="w-full bg-yellow-500 text-white px-3 py-2 rounded text-xs font-semibold hover:bg-yellow-600"
            >
              Clear All Notification State
            </button>
            <button
              onClick={() => {
                console.log('🗑️ Force clearing notified prayers state');
                // Force reload by clearing the specific notification keys from the state
                // This will be handled by the parent component
                window.dispatchEvent(new CustomEvent('clear-notified-prayers'));
                alert('Notification state cleared! Audio should now work at next prayer time.');
              }}
              className="w-full bg-purple-500 text-white px-3 py-2 rounded text-xs font-semibold hover:bg-purple-600"
            >
              Force Clear Notified Prayers
            </button>
            <button
              onClick={() => setLogs([])}
              className="w-full bg-gray-200 text-gray-700 px-3 py-1 rounded text-xs font-semibold hover:bg-gray-300"
            >
              Clear Logs
            </button>
          </div>
        </div>
      )}
    </>
  );
}
